"use client";

import Link from "next/link";
import { isSportsSlug } from "@/lib/source-url";

interface MarketRow {
  slug: string;
  title: string;
  image?: string | null;
  yesPrice: number;
  priceDelta24h?: number;
  volume24hr: number;
}

/**
 * Single-row infinite marquee of trending markets above the hero.
 *
 * The previous two-row variant felt noisy next to the showcase cards on
 * the right and the live-tape card inside it; one row is enough to read
 * alive without competing with the other motion surfaces.
 *
 * CSS-only animation, pauses on hover (so rows are actually clickable),
 * pauses completely on `prefers-reduced-motion`.
 *
 * Each cell links to `/event/[slug]` — the event-level page handles the
 * sub-market routing internally. Sending users to `/market/` would jump
 * them into a single outcome which is rarely what they want from a
 * curated trending list.
 */
export default function HeroV3TopTicker({ markets }: { markets: MarketRow[] }) {
  if (!markets || markets.length === 0) return null;

  // Duplicate so the marquee loops seamlessly (track is 2× content).
  const loop = [...markets, ...markets];

  return (
    <div className="relative border-b border-gray-200 dark:border-[#1A1A1A] bg-white/70 dark:bg-[#0a0a0b]/80 backdrop-blur-sm overflow-hidden">
      {/* Soft side fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-white dark:from-[#0a0a0b] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-white dark:from-[#0a0a0b] to-transparent" />

      <Track events={loop} speed={60} direction="left" />
    </div>
  );
}

function Track({
  events,
  speed,
  direction,
}: {
  events: MarketRow[];
  speed: number;
  direction: "left" | "right";
}) {
  return (
    <div className="group flex items-center overflow-hidden">
      <div
        className="flex items-center gap-5 py-2.5 whitespace-nowrap motion-reduce:animate-none group-hover:[animation-play-state:paused]"
        style={{
          animation: `hv3-marquee-${direction} ${speed}s linear infinite`,
        }}
      >
        {events.map((m, i) => (
          <TickerCell key={`${m.slug}-${i}`} market={m} />
        ))}
      </div>

      <style jsx global>{`
        @keyframes hv3-marquee-left {
          from {
            transform: translate3d(0, 0, 0);
          }
          to {
            transform: translate3d(-50%, 0, 0);
          }
        }
        @keyframes hv3-marquee-right {
          from {
            transform: translate3d(-50%, 0, 0);
          }
          to {
            transform: translate3d(0, 0, 0);
          }
        }
      `}</style>
    </div>
  );
}

function TickerCell({ market }: { market: MarketRow }) {
  const odds = Math.round((market.yesPrice ?? 0) * 100);
  const pct = market.priceDelta24h ?? 0;
  const up = pct >= 0;
  // /event/<slug> 301-redirects to either /polymarket/event/<slug> or
  // /sports/<slug> (for nba-/nfl-/epl-/cs2-/atp-/wta- etc.). Emit the
  // canonical URL upfront so the click pays one round-trip instead of
  // two. Saw 10+ of these in a single home → trending session.
  const href = isSportsSlug(market.slug)
    ? `/sports/${market.slug}`
    : `/polymarket/event/${market.slug}`;
  return (
    <Link
      href={href}
      prefetch={false}
      className="inline-flex items-center gap-2.5 shrink-0"
    >
      {market.image ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={market.image}
          alt={market.title || "Prediction market"}
          className="w-5 h-5 rounded-[3px] object-cover flex-shrink-0"
          loading="lazy"
        />
      ) : (
        <span className="w-5 h-5 rounded-[3px] bg-gray-200 dark:bg-white/10 flex-shrink-0" />
      )}
      <span className="font-inter-tight text-[12px] font-medium text-gray-700 dark:text-gray-300 max-w-[280px] truncate">
        {market.title}
      </span>
      <span className="font-inter-tight text-[12px] font-semibold text-gray-900 dark:text-white tabular-nums">
        {odds}¢
      </span>
      <span
        className={`font-inter-tight text-[11px] font-semibold tabular-nums ${
          up ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"
        }`}
      >
        {up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
      </span>
      <span className="text-gray-300 dark:text-white/10">·</span>
    </Link>
  );
}
